import {ICellRenderer} from 'ag-grid-community';
import {Tooltip} from 'platform/components';

import {forwardRef} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useTooltipProps} from '../../hooks/useTooltipProps';
import {ICellRendererParams} from '../../types/AgGridTypes';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getCellClassName} from '../../utils/getCellClassName';
import {getUnformattedValue} from '../../utils/getUnformattedValue';

export interface FileSizeCellRendererProps extends ICellRendererParams {
  getTooltipProps?: TooltipGetter;
}

/**
 * Renders numeric byte values as a human-readable file size string.
 *
 * @param props - AG Grid cell renderer props with the raw file size in bytes.
 * @returns Formatted file size cell content or null for unsupported values.
 */
function FileSizeCellRendererComponent(
  props: FileSizeCellRendererProps,
  _ref: React.Ref<ICellRenderer>
) {
  const className = getCellClassName(props.colDef);
  const value = getUnformattedValue(props);
  const tooltipProps = useTooltipProps(props.data, props.node, props.getTooltipProps);
  const formattedValue = props.valueFormatted ?? value;

  if (isNilOrEmpty(value)) {
    return null;
  }

  return (
    <Tooltip {...tooltipProps} label={value}>
      <div className={className}>{formattedValue}</div>
    </Tooltip>
  );
}

export const FileSizeCellRenderer = forwardRef<ICellRenderer, FileSizeCellRendererProps>(
  FileSizeCellRendererComponent
);
